import type { LineItem } from "../models/CartData";
import type { Dispatch } from "redux";
import { SET_PACKAGE_ENTRIES } from "./actions/actionTypes";

type PnAPayloadItem = {
  packageEntry: LineItem;
  oldPackageEntry?: LineItem;
  productCode: string;
};

/**
 * Best-effort debounce window for batching PnA dispatches.
 * If a product's PnA fetch resolves >80ms after the previous one (e.g. uneven network
 * latency), the timer fires an intermediate flush and subsequent completions open a new
 * cycle. This is acceptable — even partial batching (e.g. 3 flushes instead of 100)
 * dramatically reduces re-render work.
 */
const PNA_FLUSH_DELAY_MS = 80;

let buffer: PnAPayloadItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let activeDispatch: Dispatch | null = null;
let generation = 0;

/**
 * Activate the accumulator for a PnA refresh cycle.
 * Must be called before any `enqueue` calls.
 * Calling `activate` while already active resets the accumulator.
 * Returns the current generation number — callers should capture this and pass it
 * to `enqueue` so that stale payloads from a prior refresh cycle are discarded.
 */
export function activate(dispatch: Dispatch): number {
  reset();
  activeDispatch = dispatch;
  generation++;
  return generation;
}

/** Returns the current generation (for callers that need to capture it later). */
export function currentGeneration(): number {
  return generation;
}

/**
 * Enqueue PnA payload items for batched dispatch.
 * If the accumulator is not active or the generation has changed (stale async callback),
 * falls back to immediate dispatch.
 */
export function enqueue(dispatch: Dispatch, data: PnAPayloadItem[], gen: number): void {
  if (!activeDispatch || gen !== generation) {
    // Not in an active accumulation cycle, or this payload belongs to a stale cycle.
    // Dispatch immediately so data is never silently dropped.
    dispatch({ type: SET_PACKAGE_ENTRIES, payload: data });
    return;
  }

  buffer.push(...data);
  scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
  }
  flushTimer = setTimeout(flush, PNA_FLUSH_DELAY_MS);
}

function flush(): void {
  flushTimer = null;
  if (buffer.length === 0 || !activeDispatch) {
    return;
  }

  const payload = buffer;
  const d = activeDispatch;
  buffer = [];
  activeDispatch = null;
  d({ type: SET_PACKAGE_ENTRIES, payload });
}

/**
 * Reset the accumulator. Should be called when CART_LOAD_SUCCESS fires
 * to discard any stale buffered data.
 */
export function reset(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  buffer = [];
  activeDispatch = null;
}
